// ITEM HOME-GO (her go word, 2026-10-01): HER HOME ART, THE FRONT DOOR FOR EVERYONE.
// Her art IS the page (the mockup-matching law): an <img> in a positioned frame, never a CSS
// background, and every drawn button an invisible, exactly sized link at percentages of HER art's
// box (src/homeArt.js, the one map the owner preview reads too). Nothing she typeset is repeated
// as visible text; each link is NAMED by its drawn words, so keyboard and screen readers reach it.
// The frame is capped at the art's real width, so it is never stretched past its own pixels.
import { useEffect, useState, type MouseEvent } from "react";
import type { Page } from "../lib/pages";
import {
  HOME_ART_FILES, HOME_ART_PHONE_MAX, HOME_ART_ROUTE, HOME_ART_ZONES, HOME_ART_ZONES_PHONE,
  HOME_COMING_SOON_QUERY, HOME_ROOMS_ANCHOR, homeZoneHref,
} from "../homeArt.js";

type Zone = (typeof HOME_ART_ZONES)[number];

// Read ONCE, when the bundle loads, before any router effect can rewrite the address: the house
// door sends a seat the Truth gate refuses here with this query, and she is told "Coming soon".
let arrivedComingSoon = typeof window !== "undefined"
  && new URLSearchParams(window.location.search).toString().includes(HOME_COMING_SOON_QUERY);

export function scrollToRooms() {
  const el = typeof document !== "undefined" ? document.getElementById(HOME_ROOMS_ANCHOR) : null;
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

export function HomeArt({ goTo }: { goTo: (page: Page) => void }) {
  const [comingSoon, setComingSoon] = useState(false);
  useEffect(() => {
    if (arrivedComingSoon) {
      arrivedComingSoon = false;
      setComingSoon(true);
      try { window.history.replaceState(window.history.state, "", "/"); } catch { /* the notice still shows */ }
    }
    // Arriving from a link to the rooms index (the owner preview's own Explore button).
    if (typeof window !== "undefined" && window.location.hash === `#${HOME_ROOMS_ANCHOR}`) {
      window.setTimeout(scrollToRooms, 50);
    }
  }, []);

  const onZone = (zone: Zone) => (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) return;
    if (zone.id === "truth") return; // a real navigation: the server answers the house door
    event.preventDefault();
    if (zone.id === "explore-rooms") { scrollToRooms(); return; }
    if (zone.page) goTo(zone.page as Page);
  };
  const zones = (set: readonly Zone[], cls: string) => (
    <div className={`homeart__zones ${cls}`}>
      {set.map((z) => (
        <a key={z.id} className="homeart__zone" data-zone={z.id} href={homeZoneHref(z)} aria-label={z.drawn}
          onClick={onZone(z)}
          style={{ left: `${z.left}%`, top: `${z.top}%`, width: `${z.width}%`, height: `${z.height}%` }} />
      ))}
    </div>
  );
  return (
    <div className="homeart">
      <div className="homeart__frame">
        <picture>
          <source media={`(max-width: ${HOME_ART_PHONE_MAX}px)`} srcSet={`${HOME_ART_ROUTE}${HOME_ART_FILES.phone.file}`}
            width={HOME_ART_FILES.phone.width} height={HOME_ART_FILES.phone.height} />
          <img className="homeart__img" src={`${HOME_ART_ROUTE}${HOME_ART_FILES.desktop.file}`}
            width={HOME_ART_FILES.desktop.width} height={HOME_ART_FILES.desktop.height}
            alt="12th House. Ancient Wisdom for a Modern Life. Explore. Learn. Connect. Grow."
            fetchPriority="high" decoding="async" />
        </picture>
        {zones(HOME_ART_ZONES, "homeart__zones--desktop")}
        {zones(HOME_ART_ZONES_PHONE, "homeart__zones--phone")}
      </div>
      {comingSoon ? (
        <button type="button" className="homeart__soon" role="status" onClick={() => setComingSoon(false)}>
          Coming soon
        </button>
      ) : null}
    </div>
  );
}
